import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api } from "../api";
import { toast } from "sonner";

export default function AddPunchlineDialog({ open, onOpenChange, children, onCreated }) {
  const [childId, setChildId] = useState("");
  const [text, setText] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setText("");
      setDate(new Date().toISOString().slice(0, 10));
      setChildId(children?.[0]?.id || "");
    }
  }, [open, children]);

  const submit = async (e) => {
    e.preventDefault();
    if (!childId || !text.trim()) {
      toast.error("Choisis un enfant et écris une perle");
      return;
    }
    setSaving(true);
    try {
      const res = await api.post("/punchlines", {
        child_id: childId,
        text: text.trim(),
        date,
      });
      onCreated(res.data);
      toast.success("Perle ajoutée 🎉");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="add-punchline-dialog"
        className="sm:max-w-lg bg-[var(--bg)] border-[1.5px] border-[var(--ink)] rounded-none p-0 shadow-[8px_8px_0_0_#1c1b1a]"
      >
        <DialogHeader className="px-7 pt-7 pb-2">
          <DialogTitle className="font-serif-display text-3xl italic text-[var(--ink)]">
            nouvelle perle
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="px-7 pb-7 pt-2 space-y-6">
          <div>
            <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-2">
              qui l'a dit ?
            </div>
            <div className="flex flex-wrap gap-2">
              {children?.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setChildId(c.id)}
                  className={`pill ${childId === c.id ? "pill-active" : ""}`}
                  style={
                    childId === c.id
                      ? { background: c.color, color: "#1c1b1a", borderColor: "#1c1b1a" }
                      : {}
                  }
                  data-testid={`add-child-${c.name.toLowerCase()}`}
                >
                  {c.name.toLowerCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-2">
              la perle
            </div>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="« maman, regarde, j'ai inventé… »"
              className="w-full bg-transparent border-b-2 border-[var(--ink)] font-serif-display italic text-2xl text-[var(--ink)] outline-none py-2 resize-none placeholder:text-[var(--ink)]/30"
              data-testid="add-punchline-text"
            />
          </div>

          <div>
            <div className="font-ui text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)] mb-2">
              date
            </div>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent border-b-2 border-[var(--ink)] font-ui text-base text-[var(--ink)] outline-none py-2"
              data-testid="add-punchline-date"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="pill"
              data-testid="add-punchline-cancel"
            >
              annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-ink"
              data-testid="add-punchline-save"
            >
              {saving ? "sauvegarde…" : "noter →"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
